/**
 * chronos-addons.js v8.6
 *
 * v8.6 changes:
 *   - enforceRequiredButtonState() and updateATCButton() were still doing
 *     document.querySelectorAll(SELECTORS.atcButton) — a page-wide query for
 *     [name="add"]. That also matches the native Add to Cart button inside
 *     the bulk-pricing form (chronos-bulk-pricing.js hides it with
 *     display:none, it's still in the DOM with name="add"), so required-
 *     group blocking and the price-suffix text were leaking onto a button
 *     with no add-ons on it. mainForm was already correctly scoped (v8.5)
 *     for the submit listener; added an atcButtons() helper that queries
 *     within mainForm only and switched both functions to use it. No
 *     behavior change for products without a bulk-pricing form.
 *
 * v8.5 changes:
 *   - findMainForm / init: mainForm is now the form containing #chronos-addons
 *     specifically, deliberately excluding any form flagged bulk/secondary,
 *     instead of "any form with a quantity input" — the bulk form has one too.
 *   - Submit listener now binds to mainForm only, not every /cart/add form —
 *     previously it caught the "Order in Bulk" submit and collapsed it to a
 *     plain qty-1 add.
 *
 * v8.4 changes:
 *   - Required groups now DISABLE the Add to Cart button until satisfied,
 *     instead of only failing on submit. Previously a customer could click Add
 *     to Cart with no driver selected, get a silent scroll-to-error, and — if
 *     they didn't notice the red text — conclude the site was broken.
 *     The button names the first unsatisfied group ("Select Driver / SMPS to
 *     continue") rather than just going dead, so it's clear WHAT is missing.
 *   - activeGroups() extracted — the "which groups apply to the current
 *     variant" logic was duplicated between handleCartSubmit and
 *     checkReplacesMain and is now needed by the button state too. One source
 *     of truth.
 *   - The submit-time validation is deliberately KEPT. Disabled buttons don't
 *     fire click events, so it should be unreachable — but it costs nothing and
 *     covers any path where the theme re-enables the button behind our back.
 *   - Button state is re-asserted on the existing 300ms variant poll, closing
 *     the window where Symmetry's own variant handling re-enables the button
 *     before we've re-checked.
 *   - A free option (price 0) SATISFIES a required group. "No Driver / SMPS" is
 *     a deliberate choice; required means the customer must CHOOSE, not that
 *     they must buy. This matches the previous submit-time check, which tested
 *     selection count rather than price.
 *
 * v8.3 changes:
 *   - NO_OPTION_IMG: options whose label starts with "No " (No Driver / SMPS,
 *     No End Caps, No Metal Clamps, ...) now get a shared default image when
 *     they have no image of their own. Also used as the onerror fallback for
 *     every tile, replacing the old dark-square SVG placeholder.
 *   - buildGroupElement: zero-price options render NO price text at all.
 *     "Included" read as though the addon came free with the product, which is
 *     the opposite of what a "No <addon>" option means.
 *   - applySummaryLabels(): the three price-summary row labels (Base price /
 *     Add-ons / Total) can be renamed per product via a "summary_labels"
 *     object in the addon_groups metafield JSON: {base, addons, total}.
 *     Any key that's missing or blank falls back to the markup's own text, so
 *     products without the key are unaffected.
 *
 * v8.2 changes:
 *   - buildGroupElement: added "RECOMMENDED" badge rendering, mirroring the
 *     existing "BEST SELLER" badge. Driven by an option's "recommended": true
 *     flag in the addon_groups metafield JSON. Renders via .ca-badge-recommended
 *     / .ca-badge-recommended--btn classes — positioned at the opposite corner
 *     from the Best Seller badge so both can show on the same option without
 *     overlapping.
 *
 * v8.1 changes:
 *   - handleCartSubmit: added replaces_main support — when a group has
 *     "replaces_main": true in config AND customer has selected from it,
 *     the base product is skipped from cart. Only the addon (DUMMY) is added.
 *     All other products unaffected — opt-in per product via metafield JSON only.
 */

// ── fetch-cache bootstrap ──
(function(){
  if(window.theme&&window.theme.fetchCache)return;
  var s=document.createElement('script');
  s.src=window.Shopify&&window.Shopify.routes
    ? '/cdn/shop/t/85/assets/fetch-cache.js'
    : (document.querySelector('script[src*="chronos-addons"]')||{src:''})
        .src.replace('chronos-addons.js','fetch-cache.js');
  s.async=false;
  document.head.appendChild(s);
})();

(function () {
  'use strict';

  var SELECTORS = {
    wrapper:      '#chronos-addons',
    priceSummary: '#ca-price-summary',
    basePrice:    '#ca-base-price',
    addonTotal:   '#ca-addon-total',
    grandTotal:   '#ca-grand-total',
    productForm:  'form[action="/cart/add"]',
    atcButton:    '[name="add"]',
  };

  // Shown for "decline the addon" options (label starts with "No ") and as the
  // fallback when an option's image fails to load.
  var NO_OPTION_IMG = 'https://cdn.shopify.com/s/files/1/0069/3721/5087/files/NO_Option_Selected.webp?v=1781093871';

  var config            = null;
  var variantPrice      = 0;
  var variantTitle      = '';
  var selections        = {};
  var productData       = null;
  var _lastVid          = '';
  var _rendering        = false;
  var _pendingRender    = null;
  var _pendingVariantId = null;
  var mainForm          = null;
  var addonCache        = {};

  // ── Qty helper — defined first so everything below can call it ─────────────

  function getMainQty() {
    var qEl = mainForm && mainForm.querySelector('[name="quantity"]');
    return Math.max(1, parseInt((qEl && qEl.value) || '1') || 1);
  }

  // ── OPTION 1: Button disable/enable helpers ────────────────────────────────
  // These functions are called at the START and END of init() to prevent users
  // from clicking "Add to Cart" before add-ons have finished loading.

  function disableATCButtonsDuringLoad() {
    var buttons = document.querySelectorAll(SELECTORS.atcButton);
    buttons.forEach(function(btn) {
      // Skip any bulk-pricing buttons (they have their own timing logic)
      if (btn.closest('[data-bulk-pricing],.bulk-pricing,#chronos-bulk-pricing')) return;
      
      btn.disabled = true;
      btn.style.opacity = '0.6';
      btn.style.cursor = 'not-allowed';
      btn.title = '⏳ Loading product options...';
      btn.setAttribute('data-chronos-disabled-during-load', 'true');
    });
    console.log('[Chronos v8.7] ✓ ATC buttons disabled during load');
  }

  function enableATCButtonsAfterLoad() {
    var buttons = document.querySelectorAll('[data-chronos-disabled-during-load="true"]');
    buttons.forEach(function(btn) {
      btn.disabled = false;
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';
      btn.title = '';
      btn.removeAttribute('data-chronos-disabled-during-load');
    });
    console.log('[Chronos v8.7] ✓ ATC buttons re-enabled');
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  function init() {
    if (!document.querySelector(SELECTORS.wrapper)) return;

    try {
      config = window.__chronosAddonConfig__;
      if (!config || typeof config !== 'object') {
        console.warn('[Chronos] Config not found.');
        return;
      }
    } catch(e) { return; }

    // ── OPTION 1: Disable ATC button until add-ons load ──────────────────────
    console.log('[Chronos v8.7] Disabling ATC button while loading add-ons...');
    disableATCButtonsDuringLoad();

    window.__chronosDebug = function() {
      console.group('[Chronos v8.5 Debug]');
      console.log('productData loaded:', !!productData);
      console.log('_lastVid:', _lastVid);
      console.log('variantTitle:', variantTitle);
      console.log('variantPrice (paise):', variantPrice);
      console.log('mainForm found:', !!mainForm);
      console.log('mainQty:', getMainQty());
      console.log('summary_labels:', config && config.summary_labels);
      console.log('active groups:', activeGroups().map(function(g){ return g.id + (g.required ? ' (required)' : ''); }));
      console.log('unsatisfied required:', unsatisfiedRequiredGroups().map(function(g){ return g.id; }));
      var checked = document.querySelector('input.js-option:checked');
      console.log('checked input.js-option:', checked ? checked.value : 'none');
      console.groupEnd();
    };

    // ── Find the ADD-ON form specifically ──────────────────────────────────
    // A product page can have several form[action="/cart/add"] — the main
    // product form, a bulk-order form, quick-add widgets. We must bind ONLY to
    // the one our add-ons live in, or we'll intercept the submit of an
    // unrelated form (e.g. the "Order in Bulk" flow) and break it. The add-on
    // block (#chronos-addons) is rendered inside the real product form, so the
    // form that contains it — or, failing that, the nearest ancestor form — is
    // ours. We deliberately do NOT fall back to "any form with a quantity
    // input", because the bulk form has one too.
    var wrapperEl = document.querySelector(SELECTORS.wrapper);
    mainForm = wrapperEl ? wrapperEl.closest(SELECTORS.productForm) : null;
    if (!mainForm) {
      // Wrapper sits outside the form in some themes — pick the form that
      // shares the add-to-cart the summary drives, but never a form flagged as
      // a bulk/secondary form.
      document.querySelectorAll(SELECTORS.productForm).forEach(function(form) {
        if (mainForm) return;
        if (form.hasAttribute('data-ca-ignore')) return;
        if (/bulk/i.test(form.className) || form.closest('[data-bulk-pricing],.bulk-pricing,#chronos-bulk-pricing')) return;
        if (form.querySelector('input[name="quantity"]')) mainForm = form;
      });
    }

    // ── Watch quantity changes — single unified block ──────────────────────
    document.addEventListener('change', function(e) {
      if (e.target && e.target.matches('input[name="quantity"]')) {
        updatePriceSummary();
        updateATCButton();
      }
    });
    document.addEventListener('input', function(e) {
      if (e.target && e.target.matches('input[name="quantity"]')) {
        updatePriceSummary();
        updateATCButton();
      }
    });
    // Symmetry's +/- buttons update the input AFTER the click fires
    document.addEventListener('click', function(e) {
      var t = e.target;
      if (!t) return;
      var isQtyBtn = (
        t.matches('[data-quantity-minus]')   ||
        t.matches('[data-quantity-plus]')    ||
        t.matches('.js-qty-btn')             ||
        t.matches('[name="minus"]')          ||
        t.matches('[name="plus"]')           ||
        !!t.closest('.quantity__button')     ||
        !!t.closest('[data-quantity-button]')
      );
      if (isQtyBtn) {
        setTimeout(function() {
          updatePriceSummary();
          updateATCButton();
        }, 80);
      }
    });

    attachVariantListeners();

    loadProductData().then(function() {
      var checked = document.querySelector('input.js-option:checked');
      if (checked && checked.value) {
        checkVariantByTitle(checked.value);
      } else if (_pendingVariantId) {
        checkVariantId(_pendingVariantId);
        _pendingVariantId = null;
      } else {
        var v = getInitialVariant();
        if (v) applyVariant(String(v.id), v.price, v.title);
      }
    });

    // ── Submit listener — ONLY on the add-on form ──────────────────────────
    // Binding to every /cart/add form was the bug: it intercepted the bulk
    // "Order in Bulk" submit and collapsed it to a plain qty-1 add. We attach
    // to mainForm alone; other forms (bulk, quick-add) submit untouched.
    var _submitHandled = false;
    if (mainForm) {
      mainForm.addEventListener('submit', function(e) {
        if (_submitHandled) { e.preventDefault(); return; }
        _submitHandled = true;
        setTimeout(function() { _submitHandled = false; }, 2000);
        handleCartSubmit(e).catch(function(){});
      });
    }
  }

  // ── Variant listeners ──────────────────────────────────────────────────────

  function attachVariantListeners() {

    function bindJsOptionInputs() {
      document.querySelectorAll('input.js-option').forEach(function(inp) {
        if (inp._cabound) return;
        inp._cabound = true;
        inp.addEventListener('change', function() {
          if (inp.checked) checkVariantByTitle(inp.value);
        });
      });
    }
    bindJsOptionInputs();

    if (window.MutationObserver) {
      var mo = new MutationObserver(function() { bindJsOptionInputs(); });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    document.addEventListener('on:variant:change', function(e) {
      var d = e.detail || {};
      var v = d.variant || (d.id ? d : null);
      if (v && v.id) {
        if (v.price !== undefined && v.title) applyVariant(String(v.id), v.price, v.title);
        else checkVariantId(String(v.id));
      }
    });
    document.addEventListener('variant:changed', function(e) {
      var d = e.detail || {};
      var v = d.variant || (d.id ? d : null);
      if (v && v.id) checkVariantId(String(v.id));
    });

    var _polledVid = '';
    setInterval(function() {
      var inp = document.querySelector('form[action="/cart/add"] input[name="id"]');
      if (inp && inp.value && inp.value !== _polledVid) {
        _polledVid = inp.value;
        checkVariantId(inp.value);
      }
      // The theme can re-enable / relabel the Add to Cart button on its own
      // (stock states, variant swaps). Re-asserting the required-group state
      // here closes the window where the button is clickable but shouldn't be.
      enforceRequiredButtonState();
    }, 300);
  }

  function checkVariantByTitle(title) {
    if (!title) return;
    if (!productData) { _pendingVariantId = 'title:' + title; return; }

    // Pass 1: exact full title match
    var v = productData.variants.find(function(pv) { return pv.title === title; });

    // Pass 2: option1-only match (theme sends partial title from swatch input)
    if (!v) {
      v = productData.variants.find(function(pv) { return pv.option1 === title; });
    }

    if (v) applyVariant(String(v.id), v.price, v.title);
    else console.warn('[Chronos] no variant matched title:', title);
  }

  function checkVariantId(rawId) {
    var id = String(rawId || '').trim();
    if (!id) return;
    if (!productData) { _pendingVariantId = id; return; }
    var v = productData.variants.find(function(pv) { return String(pv.id) === id; });
    if (v) applyVariant(id, v.price, v.title);
  }

  function applyVariant(id, price, title) {
    if (id === _lastVid) return;
    console.log('[Chronos] variant →', title, '| price paise:', price);
    _lastVid          = id;
    _pendingVariantId = null;
    variantPrice      = price || 0;
    variantTitle      = title || '';

    document.querySelectorAll('form[action="/cart/add"] input[name="id"]').forEach(function(inp) {
      inp.value = id;
    });

    renderAll(variantTitle);

    // ── OPTION 1: Enable ATC button - add-ons are ready ──────────────────────
    console.log('[Chronos v8.7] Add-ons loaded. Enabling ATC button.');
    enableATCButtonsAfterLoad();
  }

  // ── Product data ───────────────────────────────────────────────────────────

  async function loadProductData() {
    try {
      var handle = getProductHandle();
      if (!handle) return;
      var res = await fetch('/products/' + handle + '.js');
      productData = await res.json();
    } catch(e) { console.warn('[Chronos] loadProductData failed:', e); }
  }

  function getProductHandle() {
    var c = document.querySelector('link[rel="canonical"]');
    if (c) { var m = c.href.match(/\/products\/([^?/]+)/); if (m) return m[1]; }
    var lm = window.location.pathname.match(/\/products\/([^?/]+)/);
    return lm ? lm[1] : null;
  }

  function getInitialVariant() {
    if (!productData) return null;
    var vid = new URLSearchParams(window.location.search).get('variant');
    if (vid) {
      var f = productData.variants.find(function(v) { return String(v.id) === vid; });
      if (f) return f;
    }
    return productData.variants.find(function(v) { return v.available; }) || productData.variants[0];
  }

  // ── Active groups ──────────────────────────────────────────────────────────
  // Which groups apply to the currently selected variant: every common group,
  // plus the groups of any variant_rule whose keywords appear in the variant
  // title. Extracted because handleCartSubmit, checkReplacesMain and the Add to
  // Cart button state all need the same answer, and three copies of this logic
  // drift apart.

  // Does a variant rule match the given (lowercased) variant title?
  //   match_all : EVERY token must be present (AND) — for products where the
  //               driver depends on two dimensions at once, e.g. voltage AND
  //               length ("24V" AND "10m"). A single length token can't
  //               distinguish "24V - 10m" from "12V - 10m", so those rules
  //               need all tokens.
  //   match_tags: ANY token present (OR) — the original behaviour, unchanged.
  //   match     : legacy single-token shorthand.
  // A rule with match_all takes precedence; match_tags is the fallback. Rules
  // written before this change have no match_all and behave exactly as before.
  function ruleMatches(rule, vtLower) {
    var all = rule.match_all;
    if (all && all.length) {
      return all.every(function(tok) { return vtLower.includes(String(tok).toLowerCase()); });
    }
    var tags = rule.match_tags || (rule.match ? [rule.match] : []);
    return tags.some(function(tok) { return vtLower.includes(String(tok).toLowerCase()); });
  }

  function activeGroups(vTitle) {
    if (!config) return [];
    var title = (vTitle !== undefined ? vTitle : variantTitle) || '';
    var vt    = title.toLowerCase();
    var out   = [];

    (config.common || []).forEach(function(g) { out.push(g); });

    (config.variant_rules || []).forEach(function(rule) {
      if (ruleMatches(rule, vt)) (rule.groups || []).forEach(function(g) { out.push(g); });
    });

    // Legacy shape: a flat "groups" array with no common/variant_rules split
    if (!config.common && !config.variant_rules && config.groups) {
      (config.groups || []).forEach(function(g) { out.push(g); });
    }

    return out;
  }

  // Required groups with nothing selected. A free option counts as satisfying
  // the requirement — "No Driver / SMPS" is a deliberate choice, and required
  // means the customer must CHOOSE, not that they must buy.
  function unsatisfiedRequiredGroups() {
    return activeGroups().filter(function(g) {
      if (g.required !== true) return false;
      var sel = selections[g.id];
      return !sel || sel.size === 0;
    });
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  var _renderedCommonIds = '';

  async function renderAll(vTitle) {
    if (_rendering) { _pendingRender = vTitle; return; }
    _rendering     = true;
    _pendingRender = null;

    var commonGroups  = (config && config.common) ? config.common.slice() : [];
    var variantGroups = [];

    if (config && config.variant_rules) {
      var vt = (vTitle || '').toLowerCase();
      config.variant_rules.forEach(function(rule) {
        if (ruleMatches(rule, vt)) (rule.groups || []).forEach(function(g) { variantGroups.push(g); });
      });
    }
    if (!config.common && !config.variant_rules && config.groups) {
      config.groups.forEach(function(g) { commonGroups.push(g); });
    }

    var newCommonKey  = commonGroups.map(function(g){ return g.id; }).join(',');
    var commonChanged = newCommonKey !== _renderedCommonIds;

    var toFetch = variantGroups.slice();
    if (commonChanged) toFetch = toFetch.concat(commonGroups);
    if (toFetch.length) await prefetchGroupData(toFetch);

    if (commonChanged) {
      _renderedCommonIds = newCommonKey;
      renderContainer('#ca-groups-common', commonGroups);
    }

    var keepIds = new Set(
      commonGroups.map(function(g){ return g.id; })
        .concat(variantGroups.map(function(g){ return g.id; }))
    );
    Object.keys(selections).forEach(function(k) {
      if (!keepIds.has(k)) delete selections[k];
    });

    renderContainer('#ca-groups-variant', variantGroups);

    updatePriceSummary();
    updateATCButton();
    _rendering = false;

    if (_pendingRender !== null) {
      var p = _pendingRender;
      _pendingRender = null;
      renderAll(p);
    }
  }

  function renderContainer(selector, groups) {
    var c = document.querySelector(selector);
    if (!c) return;
    c.innerHTML = '';
    if (!groups || !groups.length) return;
    groups.forEach(function(group) {
      if (!selections[group.id]) selections[group.id] = new Set();
      c.appendChild(buildGroupElement(group));
    });
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────

  async function fetchAddonData(option) {
    if (!option.handle && !option.variant_id)
      return { price: option.price || 0, image: option.image || '', variantId: null };

    var key = option.variant_id ? 'vid_' + option.variant_id : 'h_' + option.handle;
    if (addonCache[key]) return addonCache[key];

    try {
      if (option.variant_id && String(option.variant_id).match(/^\d+$/)) {
        var r = await fetch('/variants/' + option.variant_id + '.js');
        if (r.ok) {
          var d = await r.json();
          var rawP = d.price;
          var result = {
            price:     typeof rawP === 'string' ? Math.round(parseFloat(rawP) * 100) : (rawP || 0),
            image:     d.featured_image ? d.featured_image.src
                       : (d.image ? (d.image.src || d.image) : (option.image || '')),
            variantId: d.id,
            // available is false only when sold out AND not set to keep selling
            // past zero. /variants/{id}.js reflects that policy in `available`.
            available: (d.available !== false),
          };
          addonCache[key] = result;
          return result;
        }
        if (option.handle) return await fetchByHandle(option.handle, option.variant_id, key);
      } else if (option.handle) {
        return await fetchByHandle(option.handle, null, key);
      }
    } catch(e) { console.warn('[Chronos] fetch failed:', option.label, e); }

    return { price: option.price || 0, image: option.image || '', variantId: null };
  }

  async function fetchByHandle(handle, preferVid, key) {
    var r = await fetch('/products/' + handle + '.js');
    if (!r.ok) throw new Error('Not found: ' + handle);
    var d = await r.json();
    var v = d.variants[0];
    if (preferVid) {
      var f = d.variants.find(function(x){ return String(x.id) === String(preferVid); });
      if (f) v = f;
    }
    var img = '';
    if (v.featured_image && v.featured_image.src) img = v.featured_image.src;
    else if (d.featured_image) img = typeof d.featured_image === 'string' ? d.featured_image : (d.featured_image.src || '');
    else if (d.images && d.images[0]) img = typeof d.images[0] === 'string' ? d.images[0] : (d.images[0].src || '');
    if (img && img.includes('cdn.shopify.com')) img = img.replace(/(\.[a-z]+)(\?.*)?$/i, '_120x120$1');

    var rawPrice = v.price;
    var priceInCents = typeof rawPrice === 'string'
      ? Math.round(parseFloat(rawPrice) * 100)
      : (rawPrice || 0);

    var result = { price: priceInCents, image: img, variantId: v.id, available: (v.available !== false) };
    if (key) addonCache[key] = result;
    return result;
  }

  async function prefetchGroupData(groups) {
    var fetches = [];
    groups.forEach(function(group) {
      (group.options || []).forEach(function(opt) {
        opt._qty = opt._qty || 1;
        if (opt.handle || opt.variant_id) {
          fetches.push(
            fetchAddonData(opt).then(function(data) {
              if (data.price > 0)  opt._fetchedPrice     = data.price;
              if (data.image)      opt._fetchedImage     = data.image;
              if (data.variantId)  opt._fetchedVariantId = data.variantId;
              // Track availability so buildGroupElement can hide sold-out options.
              // Defaults to true when the fetch didn't report it (handle-less
              // manual options, or a failed fetch) — never hide on uncertainty.
              opt._available = (data.available !== false);
            }).catch(function(){})
          );
        }
      });
    });
    await Promise.all(fetches);
  }

  // ── Build group DOM ────────────────────────────────────────────────────────

  function buildGroupElement(group) {
    var isMulti    = group.multiSelect === true;
    var isRequired = group.required    === true;

    var div = document.createElement('div');
    div.className       = 'ca-group' + (isRequired ? ' ca-group--required' : '');
    div.dataset.groupId = group.id;

    var lbl = document.createElement('div');
    lbl.className = 'ca-group-label';
    var lh = escapeHtml(group.label);
    if (isRequired) lh += ' <span class="ca-required-badge">Required</span>';
    lh += ' <span class="ca-selected-name" id="ca-selected-' + group.id + '"></span>';
    lbl.innerHTML = lh;
    div.appendChild(lbl);

    var errEl = document.createElement('div');
    errEl.className     = 'ca-group-error';
    errEl.id            = 'ca-err-' + group.id;
    errEl.textContent   = 'Please select an option to continue';
    errEl.style.display = 'none';
    div.appendChild(errEl);

    var hasImages = group.options.some(function(o){ return o.image || o._fetchedImage; });
    var style     = group.display || (hasImages ? 'image' : 'button');
    var optWrap   = document.createElement('div');
    optWrap.className = 'ca-options--' + style;

    // ── Decide which options render, and how ─────────────────────────────────
    //   Disabled: sold-out add-ons (opt._available === false) stay VISIBLE but
    //             greyed and unselectable, labelled "Out of stock" — so the
    //             customer sees the option exists and why it can't be picked,
    //             rather than an option silently vanishing. Honours Shopify's
    //             "continue selling when out of stock" (those read as available).
    //   Decline:  a "No …" option in a multi-select group is KEPT but acts as a
    //             mutually-exclusive "none" — see the change handler below.
    //             Ticking it clears the real options; ticking a real option
    //             clears it. Real options still multi-select among themselves.
    var visible = group.options.slice();

    visible.forEach(function(opt) {
      var price     = opt._fetchedPrice || opt.price || 0;
      var image     = opt._fetchedImage  || opt.image  || '';
      var isFree    = price === 0;
      var soldOut   = opt._available === false;
      // Zero-price options render no price text at all — "Included" read as if
      // the addon came free with the product, which is the opposite of what a
      // "No <addon>" option means.
      var priceText = soldOut ? 'Out of stock' : (isFree ? '' : '+' + formatPrice(price));
      var isNone    = /^\s*no\b/i.test(opt.label || '');
      if (isNone && !image) image = NO_OPTION_IMG;
      var isBest    = opt.best_seller === true;
      var isRec     = opt.recommended === true;



      var labelEl = document.createElement('label');
      labelEl.className = 'ca-option--' + style + (soldOut ? ' ca-option--soldout' : '');

      var input = document.createElement('input');
      input.type = 'checkbox';
      input.name = 'ca_group_' + group.id;
      input.setAttribute('data-ca-decline', isNone ? '1' : '0');
      // Sold-out options are shown but must not be selectable.
      if (soldOut) input.disabled = true;

      var qtyWrap = document.createElement('div');
      qtyWrap.className     = 'ca-qty-wrap';
      qtyWrap.style.display = 'none';
      qtyWrap.innerHTML =
        '<button class="ca-qty-btn ca-qty-dec" type="button" aria-label="Decrease">−</button>' +
        '<span class="ca-qty-num">1</span>' +
        '<button class="ca-qty-btn ca-qty-inc" type="button" aria-label="Increase">+</button>';

      var qtyNum = qtyWrap.querySelector('.ca-qty-num');
      qtyWrap.querySelector('.ca-qty-dec').addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        opt._qty = Math.max(1, (opt._qty || 1) - 1);
        qtyNum.textContent = opt._qty;
        updatePriceSummary(); updateATCButton();
      });
      qtyWrap.querySelector('.ca-qty-inc').addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        opt._qty = (opt._qty || 1) + 1;
        qtyNum.textContent = opt._qty;
        updatePriceSummary(); updateATCButton();
      });

      input.addEventListener('change', function() {
        errEl.style.display = 'none';
        div.classList.remove('ca-group--error');

        if (!isMulti) {
          // Single-select: checking one clears all others.
          optWrap.querySelectorAll('.ca-qty-wrap').forEach(function(q){ q.style.display = 'none'; });
          optWrap.querySelectorAll('input[type="checkbox"]').forEach(function(cb){
            if (cb !== input) cb.checked = false;
          });
          selections[group.id] = new Set();
        } else if (input.checked) {
          // Multi-select with a mutually-exclusive "No …" decline:
          //   - ticking the decline clears every real option
          //   - ticking a real option clears the decline
          // Real options still stack among themselves.
          var others;
          if (isNone) {
            others = optWrap.querySelectorAll('input[data-ca-decline="0"]');   // clear all real
          } else {
            others = optWrap.querySelectorAll('input[data-ca-decline="1"]');   // clear the decline
          }
          others.forEach(function(cb){
            if (cb !== input && cb.checked) {
              cb.checked = false;
              var wrap = cb.closest('.ca-option-outer');
              var qw = wrap && wrap.querySelector('.ca-qty-wrap');
              if (qw) qw.style.display = 'none';
              if (cb._caOpt) selections[group.id].delete(cb._caOpt);
            }
          });
        }

        if (input.checked) {
          opt._qty = opt._qty || 1;
          qtyNum.textContent = opt._qty;
          if (!isFree) qtyWrap.style.display = 'flex';
          selections[group.id].add(opt);
        } else {
          qtyWrap.style.display = 'none';
          selections[group.id].delete(opt);
        }
        updateSelectedLabel(group.id);
        updatePriceSummary();
        updateATCButton();
      });
      // Back-reference so the exclusion loop above can remove the right option
      // object from the selections Set when it unchecks a sibling input.
      input._caOpt = opt;

      if (style === 'image') {
        var tile    = document.createElement('div');
        tile.className = 'ca-tile';
        var imgWrap = document.createElement('div');
        imgWrap.className = 'ca-tile__img-wrap';
        var img     = document.createElement('img');
        img.src     = image || blankImg();
        img.alt     = opt.label;
        img.width   = 60; img.height = 60; img.loading = 'lazy';
        img.onerror = function(){ this.src = blankImg(); };
        var chk     = document.createElement('span');
        chk.className = 'ca-tile__check';
        chk.setAttribute('aria-hidden', 'true');
        chk.innerHTML = '<svg viewBox="0 0 12 12"><polyline points="1.5,6 4.5,9.5 10.5,2.5"/></svg>';
        imgWrap.appendChild(img);
        imgWrap.appendChild(chk);
        if (isBest) {
          var badge = document.createElement('span');
          badge.className = 'ca-badge-best';
          badge.textContent = 'BEST SELLER';
          imgWrap.appendChild(badge);
        }
        if (isRec) {
          var badgeRec = document.createElement('span');
          badgeRec.className = 'ca-badge-recommended';
          badgeRec.textContent = 'RECOMMENDED';
          imgWrap.appendChild(badgeRec);
        }
        var nm = document.createElement('span');
        nm.className   = 'ca-tile-label';
        nm.textContent = opt.label;
        var pr = document.createElement('span');
        pr.className   = 'ca-tile-price';
        pr.textContent = priceText;
        tile.appendChild(imgWrap);
        tile.appendChild(nm);
        tile.appendChild(pr);
        labelEl.appendChild(input);
        labelEl.appendChild(tile);
      } else {
        var btn = document.createElement('span');
        btn.className = 'ca-btn' + (isFree ? ' ca-btn--free' : '');
        var bl  = document.createElement('span');
        bl.className   = 'ca-btn-label';
        bl.textContent = opt.label;
        var bp  = document.createElement('span');
        bp.className   = 'ca-btn-price';
        bp.textContent = priceText;
        btn.appendChild(bl);
        btn.appendChild(bp);
        if (isBest) {
          var badge2 = document.createElement('span');
          badge2.className = 'ca-badge-best ca-badge-best--btn';
          badge2.textContent = 'BEST SELLER';
          btn.appendChild(badge2);
        }
        if (isRec) {
          var badge2Rec = document.createElement('span');
          badge2Rec.className = 'ca-badge-recommended ca-badge-recommended--btn';
          badge2Rec.textContent = 'RECOMMENDED';
          btn.appendChild(badge2Rec);
        }
        labelEl.appendChild(input);
        labelEl.appendChild(btn);
      }

      var outer = document.createElement('div');
      outer.className = 'ca-option-outer';
      outer.appendChild(labelEl);
      outer.appendChild(qtyWrap);
      optWrap.appendChild(outer);
    });

    div.appendChild(optWrap);
    return div;
  }

  function blankImg() {
    return NO_OPTION_IMG;
  }

  // ── Price helpers ──────────────────────────────────────────────────────────

  function updateSelectedLabel(groupId) {
    var el  = document.getElementById('ca-selected-' + groupId);
    if (!el) return;
    var sel = selections[groupId];
    if (!sel || sel.size === 0) { el.textContent = ''; return; }
    var parts = [];
    sel.forEach(function(o) {
      var p = o._fetchedPrice || o.price || 0;
      var q = o._qty || 1;
      parts.push(o.label + (q > 1 ? ' ×' + q : '') + (p > 0 ? ' (+' + formatPrice(p * q) + ')' : ''));
    });
    el.textContent = parts.join(', ');
  }

  function addonTotalCents() {
    var t = 0;
    Object.values(selections).forEach(function(s) {
      s.forEach(function(o) { t += (o._fetchedPrice || o.price || 0) * (o._qty || 1); });
    });
    return t;
  }

  // ── Helper: check if any active selection has replaces_main ───────────────
  // NOTE: now variant-aware. The old version concatenated the groups of EVERY
  // variant_rule regardless of which variant was selected, so a replaces_main
  // group belonging to a non-matching rule could suppress the base product.
  function checkReplacesMain() {
    if (!config) return false;
    return activeGroups().some(function(group) {
      return group.replaces_main === true
        && selections[group.id]
        && selections[group.id].size > 0;
    });
  }

  // Applies the per-product summary labels from config. Runs on each update
  // rather than once at init because renderAll can rebuild the summary, and a
  // missing/blank key must fall back to the markup's default rather than
  // blanking the row.
  function applySummaryLabels() {
    var labels = (config && config.summary_labels) || {};
    var map = {
      'ca-label-base':  labels.base,
      'ca-label-addon': labels.addons,
      'ca-label-total': labels.total,
    };
    Object.keys(map).forEach(function(id) {
      var el = document.getElementById(id);
      if (!el) return;
      if (!el.dataset.caDefault) el.dataset.caDefault = el.textContent.trim();
      el.textContent = (map[id] && String(map[id]).trim()) || el.dataset.caDefault;
    });
  }

  // Base price × main qty; addon total flat (addons have their own qty)
  function updatePriceSummary() {
    var s = document.querySelector(SELECTORS.priceSummary);
    if (!s) return;
    var addonTotal = addonTotalCents();
    var qty        = getMainQty();
    if (addonTotal === 0) { s.style.display = 'none'; return; }
    s.style.display = 'block';
    applySummaryLabels();
    var b = document.querySelector(SELECTORS.basePrice);
    var a = document.querySelector(SELECTORS.addonTotal);
    var g = document.querySelector(SELECTORS.grandTotal);
    if (b) b.textContent = formatPrice(variantPrice * qty);
    if (a) a.textContent = formatPrice(addonTotal);
    if (g) g.textContent = formatPrice((variantPrice * qty) + addonTotal);
  }

  // ── Add to Cart button state ───────────────────────────────────────────────
  // Three writers compete for this button: Symmetry (stock states), our total
  // suffix, and the required-group block. dataset.caOriginalText is the shared
  // anchor — captured once, restored by whichever writer runs last.
  // dataset.caBlocked marks a disable as OURS, so we never re-enable a button
  // the theme disabled for its own reasons (sold out, unavailable variant).
  // Returns true when the button is currently blocked.

  // Scoped lookup — mirrors the mainForm fix above. A bare
  // document.querySelectorAll(SELECTORS.atcButton) also matches the native
  // Add to Cart button living inside the bulk-pricing form (chronos-bulk-
  // pricing.js just hides it with display:none, it's still in the DOM with
  // name="add"), so a global query lets required-group blocking / the price
  // suffix leak onto a button that has nothing to do with add-ons. Scope to
  // mainForm only; if mainForm wasn't found, touch nothing rather than fall
  // back to global.
  function atcButtons() {
    if (!mainForm) return [];
    return Array.prototype.slice.call(mainForm.querySelectorAll(SELECTORS.atcButton));
  }

  function enforceRequiredButtonState() {
    if (!config) return false;
    var missing = unsatisfiedRequiredGroups();
    var label   = missing.length ? (missing[0].label || 'an option') : '';

    atcButtons().forEach(function(btn) {
      if (btn.disabled && btn.dataset.caBlocked !== 'true') return;

      if (missing.length) {
        if (!btn.dataset.caOriginalText) btn.dataset.caOriginalText = btn.textContent.trim();
        btn.dataset.caBlocked = 'true';
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.textContent = 'Select ' + label + ' to continue';
      } else if (btn.dataset.caBlocked === 'true') {
        delete btn.dataset.caBlocked;
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        // Label is restored by updateATCButton immediately below.
      }
    });

    return missing.length > 0;
  }

  // Base price × main qty + addon total flat
  function updateATCButton() {
    var qty        = getMainQty();
    var addonTotal = addonTotalCents();
    var grandTotal = (variantPrice * qty) + addonTotal;

    // The required-group block takes priority over the total suffix — a blocked
    // button already carries "Select X to continue" and must not be overwritten
    // with a price the customer can't act on.
    if (enforceRequiredButtonState()) return;

    atcButtons().forEach(function(btn) {
      if (!btn.dataset.caOriginalText) btn.dataset.caOriginalText = btn.textContent.trim();
      btn.textContent = addonTotal > 0
        ? btn.dataset.caOriginalText + ' — Total ' + formatPrice(grandTotal)
        : btn.dataset.caOriginalText;
    });
  }

  // ── Cart submit ────────────────────────────────────────────────────────────

  async function handleCartSubmit(event) {
    var firstError = null;

    // ── Required group validation ──────────────────────────────────────────
    // Should be unreachable now that the button is disabled while any required
    // group is unsatisfied — disabled buttons don't submit. Kept as a backstop
    // for any path where the theme re-enables the button behind our back.
    unsatisfiedRequiredGroups().forEach(function(group) {
      var errEl = document.getElementById('ca-err-' + group.id);
      var grpEl = document.querySelector('[data-group-id="' + group.id + '"]');
      if (errEl) errEl.style.display = 'block';
      if (grpEl) { grpEl.classList.add('ca-group--error'); if (!firstError) firstError = grpEl; }
    });

    if (firstError) {
      event.preventDefault();
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var allSel = [];
    Object.values(selections).forEach(function(s){
      s.forEach(function(o){ allSel.push(o); });
    });

    var paidAddons = allSel.filter(function(o) {
      return (o._fetchedPrice || o.price || 0) > 0;
    });
    if (!paidAddons.length) return;

    event.preventDefault();

    var vid = _lastVid
      || (mainForm && mainForm.querySelector('[name="id"]') && mainForm.querySelector('[name="id"]').value)
      || '';
    if (!vid) { console.error('[Chronos] No variant ID'); return; }

    var qEl = mainForm && mainForm.querySelector('[name="quantity"]');
    var qty = qEl ? (parseInt(qEl.value) || 1) : 1;

    // ── Resolve addon variant IDs ──────────────────────────────────────────
    var resolved = await Promise.all(paidAddons.map(function(o) {
      if (o._fetchedVariantId) return Promise.resolve(o._fetchedVariantId);
      if (o.variant_id && String(o.variant_id).match(/^\d+$/))
        return Promise.resolve(parseInt(o.variant_id, 10));
      if (o.handle) {
        return fetch('/products/' + o.handle + '.js')
          .then(function(r){ return r.ok ? r.json() : null; })
          .then(function(d){ return d && d.variants[0] ? d.variants[0].id : null; })
          .catch(function(){ return null; });
      }
      return Promise.resolve(null);
    }));

    // ── Build cart items ───────────────────────────────────────────────────
    // If a group has replaces_main:true AND customer selected from it,
    // skip the base product — only the DUMMY addon goes to cart.
    // This prevents e.g. shipping 3m base + 45m addon = 48m when customer
    // only wants 45m. The DUMMY price already encodes the delta above base.
    // For all other products (no replaces_main flag) behaviour is unchanged.
    var replacesMain = checkReplacesMain();

    var items = [];
    if (!replacesMain) {
      items.push({ id: parseInt(vid, 10), quantity: qty });
    }
    resolved.forEach(function(id, i) {
      if (id) items.push({ id: id, quantity: paidAddons[i]._qty || 1 });
    });

    console.log('[Chronos] replacesMain:', replacesMain, '| Adding to cart:', items);

    try {
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items }),
      });

      if (!res.ok) {
        var errData = await res.json().catch(function(){ return {}; });
        throw new Error(errData.description || errData.message || ('HTTP ' + res.status));
      }

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

    } catch(e) {
      console.error('[Chronos] Cart add failed:', e.message);
      alert('Could not add add-ons to cart: ' + e.message + '\nPlease try again.');
    }
  }

  // ── Utils ──────────────────────────────────────────────────────────────────

  function formatPrice(cents) {
    return 'Rs.\u00a0' + (cents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  }

  function escapeHtml(s) {
    return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  // ── Boot ───────────────────────────────────────────────────────────────────
  // OPTION 3: Deferred script loading optimization
  //
  // If this script is loaded with defer (recommended), it may run:
  // 1. BEFORE DOM is ready (readyState === 'loading')   → wait for DOMContentLoaded
  // 2. AFTER DOM is ready (readyState === 'interactive') → run init immediately
  // 3. AFTER everything loads (readyState === 'complete') → run init immediately
  //
  // Using defer means the page renders faster and the script loads in parallel,
  // even though it executes after the page is visible. This trades a brief
  // moment of missing add-ons for a much faster initial paint.

  console.log('[Chronos v8.7] Boot: document.readyState =', document.readyState);

  if (document.readyState === 'loading') {
    console.log('[Chronos v8.7] DOM still loading, waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', function onDOMReady() {
      console.log('[Chronos v8.7] DOMContentLoaded fired, initializing now');
      document.removeEventListener('DOMContentLoaded', onDOMReady);
      init();
    });
  } else {
    console.log('[Chronos v8.7] DOM already ready, initializing immediately');
    init();
  }

})();